import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Helmet } from "@/components/Helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Shield, 
  Zap, 
  Eye, 
  CheckCircle2,
  Activity,
  Lock,
  RefreshCw,
  LifeBuoy,
  ArrowRight,
  Users,
  UserCheck,
  VolumeX,
  type LucideIcon
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

const Index = () => {
  const capabilities = [
    {
      icon: Zap,
      code: "MITIGATE",
      title: "Threat Detection and Containment",
      description: "Detects suspicious behaviour and applies safe, reversible containment when thresholds are exceeded.",
      stats: [
        "Detects repeated login abuse and unusual access patterns",
        "Contains suspicious activity with reversible controls",
        "Monitors integrity changes and configuration drift",
        "Alerts only after validation and context review"
      ]
    },
    {
      icon: Eye,
      code: "INTELLIGENCE",
      title: "Vulnerability Intelligence",
      description: "Monitors disclosures and exploitability signals to prioritize what matters.",
      stats: [
        "Monitors new WordPress vulnerabilities daily",
        "Prioritises risk by exploitability and exposure",
        "Correlates findings to your installed plugins and themes",
        "Shares early visibility for response planning"
      ]
    },
    {
      icon: Shield,
      code: "PROTECTION",
      title: "Application-Level Protections",
      description: "Reduces common WordPress attack paths without relying on edge controls.",
      stats: [
        "Reduces exposed entry points like XML-RPC and enumeration",
        "Contains repeated login failures with safe lockouts",
        "Applies reversible hardening toggles",
        "Works alongside customer-owned edge protection"
      ]
    },
    {
      icon: Activity,
      code: "MONITORING",
      title: "Continuous Monitoring",
      description: "Signals, validation, and escalation with human oversight where included.",
      stats: [
        "Monitors integrity, auth anomalies, and hygiene",
        "Validates alerts before escalation",
        "Aggregates portfolio visibility across sites",
        "Notifies on verified incidents and changes"
      ]
    },
    {
      icon: RefreshCw,
      code: "UPDATES",
      title: "Recovery and Hardening",
      description: "Guided remediation and safe updates that reduce future exposure.",
      stats: [
        "Guides safe core, plugin, and theme updates",
        "Tracks hygiene debt and prioritizes remediation",
        "Provides post-incident hardening checkpoints",
        "Documents actions and recommendations"
      ]
    },
    {
      icon: LifeBuoy,
      code: "SOC_RESPONSE",
      title: "SOC Operations (Premium)",
      description: "Analyst-led monitoring with incident ownership.",
      stats: [
        "Analysts investigate and validate high-risk events",
        "Containment and stabilization actions coordinated",
        "Remediation performed by ShadowScan for Premium",
        "Post-incident review and tuning"
      ]
    }
  ];

  const differentiators: { icon: LucideIcon; title: string; description: string }[] = [
    {
      icon: Shield,
      title: "WordPress-Focused Security Operations",
      description: "Purpose-built protection designed specifically for real-world WordPress environments."
    },
    {
      icon: UserCheck,
      title: "Human-Led Threat Validation",
      description: "Analyst oversight ensures alerts are accurate, relevant, and worth acting on."
    },
    {
      icon: VolumeX,
      title: "Reduced Alert Noise",
      description: "Signals are prioritised to avoid false alarms and alert fatigue."
    },
    {
      icon: Users,
      title: "Designed for Multiple Sites",
      description: "Centralised security management for agencies, MSPs, and site operators."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>ShadowScan - WordPress Security Plans With Clear Reporting</title>
        <meta
          name="description"
          content="WordPress security from free ShadowScan Guard through Basic Hosting, Essential monitored security, and Premium analyst-led response support."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "@id": "https://shadowscan.com.au/#organization",
                name: "ShadowScan",
                url: "https://shadowscan.com.au",
                logo: {
                  "@type": "ImageObject",
                  url: "https://shadowscan.com.au/og-image.png",
                  width: 1200,
                  height: 630
                },
                description: "Australian team delivering WordPress security from free ShadowScan Guard through monitored plans with human validation where included.",
                email: "support@shadowscan.com.au",
                telephone: "+61480465208",
                address: {
                  "@type": "PostalAddress",
                  addressCountry: "AU"
                },
                sameAs: [
                  "https://twitter.com/ShadowScan"
                ],
                slogan: "Managed WordPress security with clear reporting and human validation."
              },
              {
                "@type": "WebSite",
                "@id": "https://shadowscan.com.au/#website",
                url: "https://shadowscan.com.au",
                name: "ShadowScan",
                publisher: {
                  "@id": "https://shadowscan.com.au/#organization"
                },
                description: "WordPress security plans spanning free ShadowScan Guard, Basic Hosting, and monitored service levels with clear remediation guidance."
              },
              {
                "@type": "WebPage",
                "@id": "https://shadowscan.com.au/#webpage",
                url: "https://shadowscan.com.au",
                name: "ShadowScan | WordPress Security Plans",
                isPartOf: {
                  "@id": "https://shadowscan.com.au/#website"
                },
                about: {
                  "@id": "https://shadowscan.com.au/#organization"
                },
                description: "WordPress security for businesses, from free ShadowScan Guard through Basic Hosting and monitored service levels."
              },
              {
                "@type": "Service",
                serviceType: "WordPress Security Services",
                provider: {
                  "@id": "https://shadowscan.com.au/#organization"
                },
                name: "WordPress Security Plans",
                description: "WordPress security services including free ShadowScan Guard, Basic Hosting, monitored security, and analyst-led response support by plan.",
                areaServed: {
                  "@type": "Country",
                  name: "Australia"
                },
                hasOfferCatalog: {
                  "@type": "OfferCatalog",
                  name: "Security Plans",
                  itemListElement: [
                    {
                      "@type": "Offer",
                      itemOffered: {
                        "@type": "Service",
                        name: "ShadowScan Guard - Free WordPress Protection Layer",
                        description: "Free WordPress protection layer with essential local hardening and basic visibility only."
                      }
                    },
                    {
                      "@type": "Offer",
                      itemOffered: {
                        "@type": "Service",
                        name: "Basic Hosting - Hosted WordPress Onboarding",
                        description: "Hosted WordPress onboarding with ShadowScan Guard setup. It can be purchased on its own or paired with Essential or Premium when you need managed security."
                      }
                    },
                    {
                      "@type": "Offer",
                      itemOffered: {
                        "@type": "Service",
                        name: "Essential - Monitored WordPress Security",
                        description: "Continuously monitored WordPress security with vulnerability intelligence and human-validated guidance."
                      }
                    },
                    {
                      "@type": "Offer",
                      itemOffered: {
                        "@type": "Service",
                        name: "Premium - Analyst-Led Response Support",
                        description: "Human-led WordPress security with analyst oversight and priority response support within agreed scope."
                      }
                    }
                  ]
                }
              }
            ]
          })}
        </script>
      </Helmet>
      <Navigation />
      <main className="flex-grow pt-24">
        {/* Hero Section */}
        <section className="relative py-16 lg:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-20 -z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent -z-10" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl -z-10" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl -z-10" />
          
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Content */}
              <div className="space-y-6 animate-fade-in">
                <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 backdrop-blur-sm">
                  <div className="relative flex items-center justify-center">
                    <span className="absolute w-3 h-3 bg-green-500 rounded-full animate-ping opacity-75" />
                    <span className="relative w-2 h-2 bg-green-500 rounded-full" style={{ boxShadow: '0 0 10px hsl(150 70% 45% / 0.8)' }} />
                  </div>
                  <span className="font-mono text-sm text-primary">SERVICE_LIVE // CONTINUOUS MONITORING</span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-5xl font-heading font-bold leading-tight tracking-tight">
                  <span className="text-foreground">WordPress Security, From Guard to Response</span>
                  <br />
                  <span className="text-gradient">Detection. Response. Human Oversight.</span>
                </h1>
                
                <p className="text-lg text-muted-foreground leading-relaxed max-w-lg">
                  ShadowScan spans free ShadowScan Guard, Basic Hosting, and monitored service levels that reduce common attack paths and add human review where the selected plan includes it.
                </p>
                <p className="text-sm text-muted-foreground/80 max-w-lg">
                  Free ShadowScan Guard covers essential local hardening only. Basic Hosting adds hosted onboarding, while monitored security and analyst-led response require Essential or Premium.
                </p>

                <div className="flex flex-wrap gap-4 pt-2">
                  <Button size="lg" asChild className="group bg-accent hover:bg-accent/90 text-accent-foreground shadow-lg hover:shadow-accent/20 transition-all">
                    <Link to="/scan">
                      <Lock className="mr-2 h-5 w-5" />
                      Request Free Scan
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                  <Button size="lg" variant="outline" asChild className="group border-primary/50 hover:border-primary hover:bg-primary/10">
                    <Link to="/services">
                      View Services
                    </Link>
                  </Button>
                </div>
              </div>

              {/* Terminal visualization */}
              <div className="relative animate-fade-in hidden lg:block" style={{ animationDelay: '0.2s' }}>
                <div className="relative">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl blur-2xl" />
                  
                  {/* Terminal window */}
                  <div className="relative cyber-card rounded-2xl overflow-hidden border border-primary/30">
                    {/* Terminal header */}
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-secondary/50">
                      <div className="flex gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                        <div className="w-3 h-3 rounded-full bg-yellow-500" />
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                      </div>
                      <span className="ml-4 font-mono text-xs text-muted-foreground">shadowscan@soc:~</span>
                    </div>
                    
                    {/* Terminal content */}
                    <div className="p-6 font-mono text-sm space-y-3">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <span className="text-primary">$</span>
                        <span>./scan --target wordpress-site.com</span>
                      </div>
                      <div className="text-green-400">
                        [✓] Initiating limited, external scan...
                      </div>
                      <div className="text-muted-foreground">
                        [i] Checking for indicators of exposure...
                      </div>
                      <div className="text-green-400">
                        [✓] Application Protections: ACTIVE
                      </div>
                      <div className="text-green-400">
                        [✓] Access Controls: ENABLED
                      </div>
                      <div className="text-green-400">
                        [✓] Integrity Checks: RUNNING
                      </div>
                      <div className="flex items-center gap-2 text-amber-400">
                        <Activity className="h-4 w-4 animate-pulse" />
                        <span>Continuous monitoring active...</span>
                      </div>
                      <div className="pt-4 border-t border-border">
                        <span className="text-primary">STATUS:</span>
                        <span className="ml-2 text-green-400 font-bold">MONITORED</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Floating badge */}
                  <div className="absolute -top-4 -right-4 bg-green-500/20 border border-green-500/50 rounded-lg px-3 py-2 backdrop-blur-sm">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-green-400" />
                      <span className="text-xs font-mono text-green-400">MONITORED</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* Capabilities Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <div className="font-mono text-sm text-primary mb-4">// CAPABILITIES</div>
              <h2 className="text-3xl font-heading font-bold mb-4">Security Capabilities</h2>
              <p className="text-muted-foreground">
                Enterprise-grade monitoring and response powered by the #1 WordPress vulnerability discloser.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {capabilities.map((cap, index) => {
                const Icon = cap.icon;
                return (
                  <Card key={index} className="cyber-card border-border hover:border-primary/50 transition-all duration-300">
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-primary/10 border border-primary/30 rounded-lg flex items-center justify-center">
                          <Icon className="h-6 w-6 text-primary" />
                        </div>
                        <span className="font-mono text-xs text-primary/60 bg-primary/5 px-2 py-1 rounded">{cap.code}</span>
                      </div>
                      <CardTitle className="text-lg font-heading">{cap.title}</CardTitle>
                      <CardDescription className="text-sm">{cap.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {cap.stats.map((stat, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                            <span className="text-xs text-muted-foreground">{stat}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <div className="font-mono text-sm text-primary mb-4">// DIFFERENTIATORS</div>
              <h2 className="text-3xl font-heading font-bold mb-4">Why Choose ShadowScan</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {differentiators.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="flex items-start gap-4 p-6 rounded-xl bg-card border border-border">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center cyber-card rounded-2xl p-12 border-primary/30">
              <div className="font-mono text-sm text-primary mb-4">// GET_STARTED</div>
              <h2 className="text-3xl font-heading font-bold mb-4">Let's Discuss Your Security Needs</h2>
              <p className="text-muted-foreground mb-8">
                Contact our team to explore how we can support your customers.
              </p>
              <Button size="lg" asChild className="group bg-accent hover:bg-accent/90 text-accent-foreground shadow-lg hover:shadow-accent/20 transition-all">
                <Link to="/scan">
                  <Lock className="mr-2 h-5 w-5" />
                  Request Free Scan
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Index;
